import React, { useEffect, useState, useCallback } from 'react';
import { 
  Users, 
  TrendingUp, 
  Target, 
  CheckSquare,
  DollarSign,
  UserPlus,
  Activity,
  Shield,
  Search,
  MoreVertical,
  UserX,
  Crown,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  RefreshCw,
  Ticket,
  Plus,
  Copy,
  ToggleLeft,
  ToggleRight,
  Calendar,
  Eye,
  MessageSquare,
  BarChart3,
  Star,
  Bug,
  Lightbulb,
  BookOpen,
  Zap,
  Send,
  Heart,
  CheckCircle,
  Pencil
} from 'lucide-react';
import { adminApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const Admin = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [errors, setErrors] = useState([]);
  const [errorsTotal, setErrorsTotal] = useState(0);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringJob, setTriggeringJob] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: '',
    plan: 'pro',
    duration_days: 14,
    max_redemptions: -1,
    expires_at: ''
  });
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [selectedPromoRedemptions, setSelectedPromoRedemptions] = useState(null);
  const [redemptionsData, setRedemptionsData] = useState([]);
  const [editingPromo, setEditingPromo] = useState(null);
  const [editPromoForm, setEditPromoForm] = useState({ max_redemptions: 0, duration_days: 0 });  // Engagement & Feedback state
  const [engagementTrends, setEngagementTrends] = useState([]);
  const [engagementLoading, setEngagementLoading] = useState(false);
  const [feedback, setFeedback] = useState([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [seedingModules, setSeedingModules] = useState(false);
  // Beta Management state
  const [betaTesters, setBetaTesters] = useState([]);
  const [betaStats, setBetaStats] = useState(null);
  const [betaFeedback, setBetaFeedback] = useState([]);
  const [betaFeedbackSummary, setBetaFeedbackSummary] = useState(null);
  const [betaLoading, setBetaLoading] = useState(false);
  const [bulkInviteEmails, setBulkInviteEmails] = useState('');
  const [bulkInvitePromo, setBulkInvitePromo] = useState('BETA2026');
  const [sendingInvites, setSendingInvites] = useState(false);
  const [inviteResults, setInviteResults] = useState(null);
  // Client Management state
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [clientForm, setClientForm] = useState({ email: '', name: '', password: '', plan: 'enterprise', send_welcome_email: true });
  const [creatingClient, setCreatingClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);
  const [showAddPriority, setShowAddPriority] = useState(false);
  const [priorityForm, setPriorityForm] = useState({ title: '', description: '', target_date: '' });
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [goalForm, setGoalForm] = useState({ title: '', description: '', category: 'strategic', goal_type: 'outcome', priority_id: '', due_date: '', key_results: [] });
  const [showAddTask, setShowAddTask] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', due_date: '', goal_id: '', recurring: '' });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const pageSize = 10;

  const loadSchedulerStatus = useCallback(async () => {
    try {
      const res = await adminApi.getSchedulerStatus();
      setSchedulerStatus(res.data);
    } catch (error) {
      console.error('Failed to load scheduler status:', error);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const statsRes = await adminApi.getStats();
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to load admin stats:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const params = {
        skip: page * pageSize,
        limit: pageSize,
      };
      if (searchQuery) params.search = searchQuery;
      if (planFilter !== 'all') params.plan = planFilter;
      
      const res = await adminApi.getUsers(params);
      setUsers(res.data.users || []);
      setTotalUsers(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }, [page, searchQuery, planFilter, pageSize]);

  useEffect(() => {
    loadData();
    loadSchedulerStatus();
  }, [loadData, loadSchedulerStatus]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const loadPromoCodes = async () => {
    setPromoLoading(true);
    try {
      const res = await adminApi.getPromoCodes();
      setPromoCodes(res.data);
    } catch (error) {
      console.error('Failed to load promo codes:', error);
      toast.error('Failed to load promo codes');
    } finally {
      setPromoLoading(false);
    }
  };

  const loadEngagementTrends = async () => {
    setEngagementLoading(true);
    try {
      const res = await adminApi.getEngagementTrends(14);
      setEngagementTrends(res.data.trends || []);
    } catch (error) {
      console.error('Failed to load engagement trends:', error);
    } finally {
      setEngagementLoading(false);
    }
  };

  const loadFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const res = await adminApi.getFeedback(0, 50);
      setFeedback(res.data.feedback || []);
      setFeedbackTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load feedback:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleCreatePromoCode = async (e) => {
    e.preventDefault();
    if (!promoForm.code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }
    
    setCreatingPromo(true);
    try {
      const data = {
        ...promoForm,
        code: promoForm.code.toUpperCase(),
        expires_at: promoForm.expires_at || null
      };
      await adminApi.createPromoCode(data);
      toast.success(`Promo code ${data.code} created!`);
      setShowCreatePromo(false);
      setPromoForm({ code: '', plan: 'pro', duration_days: 14, max_redemptions: -1, expires_at: '' });
      loadPromoCodes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promo code');
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleTogglePromoCode = async (code) => {
    try {
      const res = await adminApi.togglePromoCode(code);
      toast.success(res.data.message);
      loadPromoCodes();
    } catch (error) {
      toast.error('Failed to toggle promo code');
    }
  };

  const handleDeletePromoCode = async (code) => {
    if (!window.confirm(`Delete promo code ${code}?`)) return;
    try {
      await adminApi.deletePromoCode(code);
      toast.success('Promo code deleted');
      loadPromoCodes();
    } catch (error) {
      toast.error('Failed to delete promo code');
    }
  };

  const handleEditPromoCode = (promo) => {
    setEditingPromo(promo.code);
    setEditPromoForm({
      max_redemptions: promo.max_redemptions,
      duration_days: promo.duration_days
    });
  };

  const handleSavePromoCode = async () => {
    try {
      await adminApi.updatePromoCode(editingPromo, editPromoForm);
      toast.success('Promo code updated');
      setEditingPromo(null);
      loadPromoCodes();
    } catch (error) {
      toast.error('Failed to update promo code');
    }
  };

  const handleViewRedemptions = async (code) => {
    try {
      const res = await adminApi.getPromoRedemptions(code);
      setRedemptionsData(res.data);
      setSelectedPromoRedemptions(code);
    } catch (error) {
      toast.error('Failed to load redemptions');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const loadBetaData = async () => {
    setBetaLoading(true);
    try {
      const [testersRes, feedbackRes] = await Promise.all([
        adminApi.getBetaTesters(),
        adminApi.getBetaFeedback()
      ]);
      setBetaTesters(testersRes.data.testers || []);
      setBetaStats(testersRes.data.stats || null);
      setBetaFeedback(feedbackRes.data.feedback || []);
      setBetaFeedbackSummary(feedbackRes.data.summary || null);
    } catch (error) {
      console.error('Failed to load beta data:', error);
      toast.error('Failed to load beta data');
    } finally {
      setBetaLoading(false);
    }
  };

  const handleBulkInvite = async () => {
    const emails = bulkInviteEmails.split('\n').map(e => e.trim()).filter(e => e && e.includes('@'));
    if (emails.length === 0) {
      toast.error('Please enter valid email addresses');
      return;
    }
    
    setSendingInvites(true);
    try {
      const res = await adminApi.bulkInviteBeta(emails, bulkInvitePromo);
      setInviteResults(res.data.results);
      toast.success(`Processed ${res.data.results.length} invitations`);
      setBulkInviteEmails('');
      loadBetaData(); // Refresh testers list
    } catch (error) {
      toast.error('Failed to send invites: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingInvites(false);
    }
  };

  const handleTriggerJob = async (jobType) => {
    setTriggeringJob(jobType);
    try {
      if (jobType === 'daily') {
        await adminApi.triggerDailyPrompts();
        toast.success('Daily prompt emails triggered');
      } else if (jobType === 'weekly') {
        await adminApi.triggerWeeklySummaries();
        toast.success('Weekly summary emails triggered');
      }
    } catch (error) {
      toast.error('Failed to trigger job: ' + error.message);
    } finally {
      setTriggeringJob(null);
    }
  };

  const handleViewUser = async (userId) => {
    try {
      const res = await adminApi.getUserDetails(userId);
      setUserDetails(res.data);
      setSelectedUser(userId);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };

  const handleUpdatePlan = async (userId, newPlan) => {
    try {
      await adminApi.updateUserPlan(userId, newPlan);
      toast.success(`Plan updated to ${newPlan}`);
      loadUsers();
      loadData();
      if (selectedUser === userId) {
        handleViewUser(userId);
      }
    } catch (error) {
      toast.error('Failed to update plan');
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const res = await adminApi.toggleUserActive(userId);
      toast.success(res.data.message);
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle user status');
    }
  };

  const handleToggleAdmin = async (userId) => {
    try {
      const res = await adminApi.toggleUserAdmin(userId);
      toast.success(res.data.message);
      loadUsers();
    } catch (error) {
      toast.error('Failed to toggle admin status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete ${userName}? This cannot be undone.`)) return;
    try {
      await adminApi.deleteUser(userId);
      toast.success('User deleted');
      loadUsers();
      loadData();
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // ============ CLIENT MANAGEMENT FUNCTIONS ============
  
  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!clientForm.email.trim() || !clientForm.name.trim() || !clientForm.password.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreatingClient(true);
    try {
      const res = await adminApi.createClient(clientForm);
      toast.success(`Client account created for ${clientForm.name}`);
      setShowCreateClient(false);
      setClientForm({ email: '', name: '', password: '', plan: 'enterprise', send_welcome_email: true });
      loadUsers();
      // Automatically open the new client's profile
      setSelectedClient(res.data.user_id);
      loadClientProfile(res.data.user_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreatingClient(false);
    }
  };

  const loadClientProfile = async (userId) => {
    setClientLoading(true);
    try {
      const res = await adminApi.getClientFullProfile(userId);
      setClientProfile(res.data);
    } catch (error) {
      toast.error('Failed to load client profile');
      setClientProfile(null);
    } finally {
      setClientLoading(false);
    }
  };

  const handleUpdateClientStrategic = async (e) => {
    e.preventDefault();
    if (!selectedClient || !clientProfile) return;
    
    try {
      await adminApi.updateClientStrategic(selectedClient, {
        vision_title: clientProfile.strategic_objectives?.vision?.title || '',
        vision_description: clientProfile.strategic_objectives?.vision?.description || '',
        mission_title: clientProfile.strategic_objectives?.mission?.title || '',
        mission_description: clientProfile.strategic_objectives?.mission?.description || ''
      });
      toast.success('Strategic objectives updated');
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to update strategic objectives');
    }
  };

  const handleAddClientPriority = async (e) => {
    e.preventDefault();
    if (!selectedClient || !priorityForm.title.trim()) return;
    
    try {
      await adminApi.addClientPriority(selectedClient, priorityForm);
      toast.success('Priority added');
      setShowAddPriority(false);
      setPriorityForm({ title: '', description: '', target_date: '' });
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to add priority');
    }
  };

  const handleDeleteClientPriority = async (priorityId) => {
    if (!window.confirm('Delete this priority?')) return;
    
    try {
      await adminApi.deleteClientPriority(selectedClient, priorityId);
      toast.success('Priority deleted');
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to delete priority');
    }
  };

  const handleAddClientGoal = async (e) => {
    e.preventDefault();
    if (!selectedClient || !goalForm.title.trim()) return;
    
    try {
      await adminApi.addClientGoal(selectedClient, goalForm);
      toast.success('Goal added');
      setShowAddGoal(false);
      setGoalForm({ title: '', description: '', category: 'strategic', goal_type: 'outcome', priority_id: '', due_date: '', key_results: [] });
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to add goal');
    }
  };

  const handleDeleteClientGoal = async (goalId) => {
    if (!window.confirm('Delete this goal?')) return;
    
    try {
      await adminApi.deleteClientGoal(selectedClient, goalId);
      toast.success('Goal deleted');
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to delete goal');
    }
  };

  const handleAddClientTask = async (e) => {
    e.preventDefault();
    if (!selectedClient || !taskForm.title.trim()) return;
    
    try {
      await adminApi.addClientTask(selectedClient, taskForm);
      toast.success('Task added');
      setShowAddTask(false);
      setTaskForm({ title: '', description: '', priority: 'medium', due_date: '', goal_id: '', recurring: '' });
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleDeleteClientTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    
    try {
      await adminApi.deleteClientTask(selectedClient, taskId);
      toast.success('Task deleted');
      loadClientProfile(selectedClient);
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleResetClientPassword = async () => {
    if (!selectedClient || !newPassword.trim()) return;
    
    try {
      await adminApi.resetClientPassword(selectedClient, newPassword);
      toast.success('Password reset successfully');
      setShowResetPassword(false);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // ============ END CLIENT MANAGEMENT ============

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-400';
      case 'pro': return 'bg-[#26C6DA]/10 text-[#26C6DA]';
      default: return 'bg-slate-700/30 text-slate-500 dark:text-slate-400';
    }
  };

  const loadErrors = async () => {
    setErrorsLoading(true);
    try {
      const res = await adminApi.getErrors();
      setErrors(res.data.errors || []);
      setErrorsTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load errors:', error);
    } finally {
      setErrorsLoading(false);
    }
  };

  const clearErrors = async () => {
    if (!window.confirm('Are you sure you want to clear all error logs?')) return;
    try {
      await adminApi.clearErrors();
      setErrors([]);
      setErrorsTotal(0);
      toast.success('Error logs cleared');
    } catch (error) {
      toast.error('Failed to clear errors');
    }
  };

  const handleSeedLearningModules = async () => {
    if (!window.confirm('This will populate learning modules and daily prompts in the database. Continue?')) return;
    setSeedingModules(true);
    try {
      const res = await adminApi.seedLearningModules();
      const data = res.data;
      if (data.modules_created > 0 || data.prompts_created > 0) {
        toast.success(`Seeded ${data.modules_created} modules and ${data.prompts_created} prompts!`);
      } else {
        toast.info(`Data already exists: ${data.modules_existing} modules, ${data.prompts_existing} prompts`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed learning modules');
    } finally {
      setSeedingModules(false);
    }
  };

  const handleForceReseed = async () => {
    if (!window.confirm('WARNING: This will DELETE all existing modules and prompts, then recreate them. User progress will be preserved. Continue?')) return;
    setSeedingModules(true);
    try {
      const res = await adminApi.forceReseedModules();
      const data = res.data;
      toast.success(`Force reseed complete! Deleted ${data.deleted_modules} modules, created ${data.created_modules} new modules.`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force reseed');
    } finally {
      setSeedingModules(false);
    }
  };

  const handleDebugModules = async () => {
    try {
      const res = await adminApi.debugModules();
      const data = res.data;
      const moduleList = data.modules.map(m => `${m.title}: published=${m.is_published}`).join('\n');
      alert(`Total: ${data.total_modules}\nPublished: ${data.published_modules}\n\nModules:\n${moduleList}`);
    } catch (error) {
      toast.error('Failed to fetch debug info');
    }
  };

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Shield className="w-16 h-16 text-slate-600 dark:text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2">Admin Access Required</h2>
          <p className="text-slate-500 dark:text-slate-400">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="admin-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
          Admin Dashboard
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Manage users, monitor platform activity, and oversee subscriptions.
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#1F2937] flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="clients">
            <UserPlus className="w-4 h-4 mr-1" /> Clients
          </TabsTrigger>
          <TabsTrigger value="beta" onClick={() => loadBetaData()}>
            <Zap className="w-4 h-4 mr-1" /> Beta Testers
          </TabsTrigger>
          <TabsTrigger value="engagement" onClick={() => loadEngagementTrends()}>
            <BarChart3 className="w-4 h-4 mr-1" /> Engagement
          </TabsTrigger>
          <TabsTrigger value="feedback" onClick={() => loadFeedback()}>
            <MessageSquare className="w-4 h-4 mr-1" /> Feedback {feedbackTotal > 0 && <span className="ml-1 px-1.5 py-0.5 bg-[#26C6DA]/20 text-[#26C6DA] text-xs rounded-full">{feedbackTotal}</span>}
          </TabsTrigger>
          <TabsTrigger value="promo" onClick={() => loadPromoCodes()}>Promo Codes</TabsTrigger>
          <TabsTrigger value="scheduler">Scheduler</TabsTrigger>
          <TabsTrigger value="errors" onClick={() => loadErrors()}>
            Error Logs {errorsTotal > 0 && <span className="ml-1 px-1.5 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">{errorsTotal}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-6">
          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="stat-total-users">
                <div className="flex items-center justify-between mb-3">
                  <Users className="w-5 h-5 text-[#26C6DA]" />
                  <span className="text-xs text-emerald-600 font-medium">+{stats.recent_signups} this week</span>
                </div>
                <p className="text-3xl font-bold text-[#26C6DA] font-mono">{stats.total_users}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Users</p>
              </div>
              
              <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="stat-active-today">
                <div className="flex items-center justify-between mb-3">
                  <Activity className="w-5 h-5 text-emerald-500" />
                </div>
                <p className="text-3xl font-bold text-[#26C6DA] font-mono">{stats.active_today}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Active Today</p>
              </div>
              
              <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-5" data-testid="stat-teams">
                <div className="flex items-center justify-between mb-3">
                  <Target className="w-5 h-5 text-[#26C6DA]" />
                </div>
                <p className="text-3xl font-bold text-[#26C6DA] font-mono">{stats.total_teams}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Teams</p>
              </div>
              
              <div className="bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-2xl p-5 text-white" data-testid="stat-mrr">
                <div className="flex items-center justify-between mb-3">
                  <DollarSign className="w-5 h-5 text-white/80" />
                </div>
                <p className="text-3xl font-bold font-mono">${stats.mrr}</p>
                <p className="text-sm text-white/70">Monthly Revenue</p>
              </div>
            </div>
          )}

          {/* Plan Distribution */}
          {stats && stats.users_by_plan && (
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
                Users by Plan
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-slate-500 dark:text-slate-400 font-mono">{stats.users_by_plan.free || 0}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Free</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">$0/mo</p>
                </div>
                <div className="p-4 bg-[#26C6DA]/5 rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#26C6DA] font-mono">{stats.users_by_plan.pro || 0}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Pro</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">$19/mo</p>
                </div>
                <div className="p-4 bg-purple-500/10 rounded-xl text-center">
                  <p className="text-2xl font-bold text-purple-400 font-mono">{stats.users_by_plan.enterprise || 0}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Enterprise</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">$49/mo</p>
                </div>
              </div>
            </div>
          )}

          {/* Feature Adoption */}
          {stats && stats.feature_adoption && (
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <BarChart3 className="w-5 h-5 text-[#26C6DA]" /> Feature Adoption
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div className="text-center p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl">
                  <Target className="w-5 h-5 mx-auto mb-2 text-emerald-400" />
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.feature_adoption.goals}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Using Goals</p>
                </div>
                <div className="text-center p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl">
                  <CheckSquare className="w-5 h-5 mx-auto mb-2 text-blue-400" />
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.feature_adoption.tasks}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Using Tasks</p>
                </div>
                <div className="text-center p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl">
                  <BookOpen className="w-5 h-5 mx-auto mb-2 text-purple-400" />
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.feature_adoption.modules}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Learning</p>
                </div>
                <div className="text-center p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl">
                  <MessageSquare className="w-5 h-5 mx-auto mb-2 text-amber-400" />
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.feature_adoption.prompts}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Daily Prompts</p>
                </div>
                <div className="text-center p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl">
                  <Zap className="w-5 h-5 mx-auto mb-2 text-[#26C6DA]" />
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.feature_adoption.coach}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">AI Coach</p>
                </div>
                <div className="text-center p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl">
                  <Calendar className="w-5 h-5 mx-auto mb-2 text-rose-400" />
                  <p className="text-xl font-bold text-slate-800 dark:text-slate-100">{stats.feature_adoption.calendar}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Calendar</p>
                </div>
              </div>
            </div>
          )}

          {/* System Management */}
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Shield className="w-5 h-5 text-[#26C6DA]" /> System Management
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                <div className="flex flex-col gap-3">
                  <div>
                    <h4 className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-purple-400" />
                      Seed Modules
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Add modules if empty
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleSeedLearningModules}
                    disabled={seedingModules}
                    className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full w-full"
                    data-testid="seed-modules-btn"
                  >
                    {seedingModules ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Seed Data'}
                  </Button>
                </div>
              </div>
              
              <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                <div className="flex flex-col gap-3">
                  <div>
                    <h4 className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-orange-400" />
                      Force Reseed
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Delete & recreate all modules
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleForceReseed}
                    disabled={seedingModules}
                    className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-full"
                    data-testid="force-reseed-btn"
                  >
                    {seedingModules ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Force Reseed'}
                  </Button>
                </div>
              </div>
              
              <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                <div className="flex flex-col gap-3">
                  <div>
                    <h4 className="font-medium text-slate-800 dark:text-slate-100 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      Debug Modules
                    </h4>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Check module status in DB
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleDebugModules}
                    variant="outline"
                    className="rounded-full w-full border-slate-300 dark:border-slate-600"
                    data-testid="debug-modules-btn"
                  >
                    Debug
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Engagement Tab */}
        <TabsContent value="engagement" className="mt-6">
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <BarChart3 className="w-5 h-5 text-[#26C6DA]" /> Engagement Trends (Last 14 Days)
            </h3>
            
            {engagementLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[#26C6DA]" />
              </div>
            ) : engagementTrends.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-[#1F2937]">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Date</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Active Users</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">New Users</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Goals Created</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Tasks Created</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-600 dark:text-slate-400">Prompts</th>
                    </tr>
                  </thead>
                  <tbody>
                    {engagementTrends.map((day, idx) => (
                      <tr key={day.date} className={idx % 2 === 0 ? 'bg-slate-50 dark:bg-[#1A1A2E]/50' : ''}>
                        <td className="py-3 px-4 text-sm text-slate-800 dark:text-slate-200">{new Date(day.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-medium ${day.active_users > 0 ? 'text-emerald-500' : 'text-slate-400'}`}>{day.active_users}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-medium ${day.new_users > 0 ? 'text-blue-500' : 'text-slate-400'}`}>{day.new_users}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-medium ${day.goals_created > 0 ? 'text-purple-500' : 'text-slate-400'}`}>{day.goals_created}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-medium ${day.tasks_created > 0 ? 'text-amber-500' : 'text-slate-400'}`}>{day.tasks_created}</span>
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={`font-medium ${day.prompt_responses > 0 ? 'text-[#26C6DA]' : 'text-slate-400'}`}>{day.prompt_responses}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">No engagement data available</p>
            )}
          </div>
        </TabsContent>

        {/* Feedback Tab */}
        <TabsContent value="feedback" className="mt-6">
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <MessageSquare className="w-5 h-5 text-[#26C6DA]" /> Beta Feedback ({feedbackTotal})
            </h3>
            
            {feedbackLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[#26C6DA]" />
              </div>
            ) : feedback.length > 0 ? (
              <div className="space-y-4">
                {feedback.map((item) => (
                  <div key={item.id} className="p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.feedback_type === 'bug' ? 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400' :
                          item.feedback_type === 'feature' ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400' :
                          item.feedback_type === 'improvement' ? 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400' :
                          'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400'
                        }`}>
                          {item.feedback_type === 'bug' && <Bug className="w-3 h-3 inline mr-1" />}
                          {item.feedback_type === 'feature' && <Lightbulb className="w-3 h-3 inline mr-1" />}
                          {item.feedback_type === 'improvement' && <Star className="w-3 h-3 inline mr-1" />}
                          {item.feedback_type}
                        </span>
                        {item.rating && (
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className={`w-3 h-3 ${star <= item.rating ? 'text-amber-400 fill-current' : 'text-slate-300'}`} />
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {new Date(item.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-slate-800 dark:text-slate-200 mb-2">{item.message}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                      <span>{item.user_email}</span>
                      {item.page_url && <span className="truncate max-w-xs">{item.page_url}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">No feedback received yet</p>
            )}
          </div>
        </TabsContent>

        {/* Beta Testers Tab */}
        <TabsContent value="beta" className="mt-6 space-y-6">
          {/* Stats Cards */}
          {betaStats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-[#26C6DA] font-mono">{betaStats.total_testers}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Total Testers</p>
              </div>
              <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-emerald-500 font-mono">{betaStats.active_testers}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Active (Score 20+)</p>
              </div>
              <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-purple-500 font-mono">{betaStats.avg_engagement_score}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Avg Engagement</p>
              </div>
              <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-500 font-mono">{betaStats.feedback_submitted}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Feedback Received</p>
              </div>
              <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-4 text-center">
                <p className="text-3xl font-bold text-orange-500 font-mono">{betaStats.feedback_pending}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Awaiting Feedback</p>
              </div>
            </div>
          )}

          {/* Bulk Invite Section */}
          <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <UserPlus className="w-5 h-5 text-[#26C6DA]" />
              Bulk Invite Beta Testers
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Email Addresses (one per line)</Label>
                  <textarea
                    value={bulkInviteEmails}
                    onChange={(e) => setBulkInviteEmails(e.target.value)}
                    placeholder="user1@example.com&#10;user2@example.com&#10;user3@example.com"
                    className="w-full h-40 mt-2 p-3 rounded-xl border border-slate-200 dark:border-[#1F2937] bg-slate-50 dark:bg-[#1A1A2E] text-slate-800 dark:text-slate-200 resize-none"
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Promo Code to Apply</Label>
                  <Input
                    value={bulkInvitePromo}
                    onChange={(e) => setBulkInvitePromo(e.target.value.toUpperCase())}
                    className="mt-2 bg-slate-50 dark:bg-[#1A1A2E]"
                  />
                </div>
                <Button
                  onClick={handleBulkInvite}
                  disabled={sendingInvites || !bulkInviteEmails.trim()}
                  className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                >
                  {sendingInvites ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Send Invitations</>
                  )}
                </Button>
              </div>
              
              {/* Invite Results */}
              {inviteResults && inviteResults.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-700 dark:text-slate-300">Results - Credentials to Share</Label>
                    {inviteResults.some(r => r.temp_password) && (
                      <button
                        onClick={() => {
                          const credentials = inviteResults
                            .filter(r => r.temp_password)
                            .map(r => `Email: ${r.email}\nPassword: ${r.temp_password}\nLogin: https://strategicarchitect.net/login`)
                            .join('\n\n---\n\n');
                          copyToClipboard(credentials);
                          toast.success('All credentials copied!');
                        }}
                        className="text-sm px-3 py-1 bg-[#26C6DA] text-black rounded-lg hover:bg-[#0891B2] transition-colors flex items-center gap-1"
                      >
                        <Copy className="w-4 h-4" />
                        Copy All Credentials
                      </button>
                    )}
                  </div>
                  <div className="h-72 overflow-y-auto p-3 rounded-xl border-2 border-[#26C6DA]/50 bg-slate-50 dark:bg-[#1A1A2E]">
                    {inviteResults.map((result, idx) => (
                      <div key={idx} className={`p-3 mb-3 rounded-lg ${
                        result.status === 'created' ? 'bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700' :
                        result.status === 'resent' ? 'bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700' :
                        result.status === 'upgraded' ? 'bg-purple-100 dark:bg-purple-900/30 border border-purple-300 dark:border-purple-700' :
                        result.status === 'exists' ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700' :
                        'bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className={`font-semibold ${
                            result.status === 'created' ? 'text-green-800 dark:text-green-200' :
                            result.status === 'resent' ? 'text-blue-800 dark:text-blue-200' :
                            result.status === 'upgraded' ? 'text-purple-800 dark:text-purple-200' :
                            result.status === 'exists' ? 'text-yellow-800 dark:text-yellow-200' :
                            'text-red-800 dark:text-red-200'
                          }`}>{result.email}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            result.status === 'created' ? 'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200' :
                            result.status === 'resent' ? 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200' :
                            result.status === 'upgraded' ? 'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200' :
                            result.status === 'exists' ? 'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200' :
                            'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                          }`}>{result.status === 'resent' ? 'RESENT' : result.status === 'upgraded' ? 'UPGRADED' : result.status}</span>
                        </div>
                        {result.temp_password && (
                          <div className="mt-2 p-2 bg-white dark:bg-black/30 rounded-lg border border-slate-200 dark:border-slate-600">
                            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">Temporary Password (share with user):</div>
                            <div className="flex items-center justify-between">
                              <code className="text-lg font-bold text-[#26C6DA]">{result.temp_password}</code>
                              <button 
                                onClick={() => {
                                  copyToClipboard(`Email: ${result.email}\nPassword: ${result.temp_password}\nLogin: https://strategicarchitect.net/login`);
                                  toast.success('Credentials copied!');
                                }}
                                className="px-2 py-1 bg-[#26C6DA]/20 hover:bg-[#26C6DA]/40 text-[#26C6DA] rounded flex items-center gap-1 text-sm transition-colors"
                              >
                                <Copy className="w-4 h-4" />
                                Copy
                              </button>
                            </div>
                          </div>
                        )}
                        {!result.temp_password && (
                          <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">{result.message}</div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    ⚠️ Email delivery may be delayed or blocked by spam filters. Share credentials manually if needed.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Beta Testers List */}
          <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-[#1F2937]">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Beta Testers ({betaTesters.length})
              </h3>
            </div>
            {betaLoading ? (
              <div className="p-8 text-center">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#26C6DA]" />
              </div>
            ) : betaTesters.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 dark:bg-[#1A1A2E]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400">User</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">Goals</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">Tasks</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">AI Chats</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">Modules</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">Score</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">Feedback</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">Expires</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-[#1F2937]">
                    {betaTesters.map((tester) => (
                      <tr key={tester.id} className="hover:bg-slate-50 dark:hover:bg-[#1A1A2E]/50">
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-slate-800 dark:text-slate-200">{tester.name}</p>
                            <p className="text-xs text-slate-500">{tester.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-slate-700 dark:text-slate-300">{tester.metrics.goals_created}</span>
                          {tester.metrics.goals_completed > 0 && (
                            <span className="text-green-500 text-xs ml-1">({tester.metrics.goals_completed} done)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-slate-700 dark:text-slate-300">{tester.metrics.tasks_created}</span>
                          {tester.metrics.tasks_completed > 0 && (
                            <span className="text-green-500 text-xs ml-1">({tester.metrics.tasks_completed} done)</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                          {tester.metrics.ai_messages}
                        </td>
                        <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">
                          {tester.metrics.modules_completed}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            tester.engagement_score >= 60 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                            tester.engagement_score >= 30 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                            'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                          }`}>
                            {tester.engagement_score}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {tester.feedback_submitted ? (
                            <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center text-xs text-slate-500">
                          {tester.plan_expires_at ? new Date(tester.plan_expires_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-slate-500 dark:text-slate-400 py-8">No beta testers yet. Send some invites!</p>
            )}
          </div>

          {/* Beta Feedback Summary */}
          {betaFeedbackSummary && betaFeedbackSummary.total_responses > 0 && (
            <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Star className="w-5 h-5 text-yellow-400" />
                Beta Feedback Summary
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl">
                  <p className="text-2xl font-bold text-yellow-500">{betaFeedbackSummary.avg_overall_rating}/5</p>
                  <p className="text-sm text-slate-500">Overall Rating</p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl">
                  <p className="text-2xl font-bold text-blue-500">{betaFeedbackSummary.avg_ease_of_use}/5</p>
                  <p className="text-sm text-slate-500">Ease of Use</p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl">
                  <p className="text-2xl font-bold text-purple-500">{betaFeedbackSummary.avg_feature_completeness}/5</p>
                  <p className="text-sm text-slate-500">Feature Complete</p>
                </div>
                <div className="text-center p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl">
                  <p className="text-2xl font-bold text-green-500">{betaFeedbackSummary.would_recommend_percent}%</p>
                  <p className="text-sm text-slate-500">Would Recommend</p>
                </div>
              </div>
              
              {/* Individual Feedback */}
              <div className="space-y-4">
                {betaFeedback.map((fb) => (
                  <div key={fb.id} className="p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-slate-800 dark:text-slate-200">{fb.user_name || fb.user_email}</span>
                      <div className="flex items-center gap-1">
                        {[1,2,3,4,5].map(star => (
                          <Star key={star} className={`w-4 h-4 ${star <= fb.overall_rating ? 'fill-yellow-400 text-yellow-400' : 'text-slate-300'}`} />
                        ))}
                      </div>
                    </div>
                    {fb.favorite_feature && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                        <Heart className="w-3 h-3 inline mr-1 text-pink-500" />
                        <strong>Favorite:</strong> {fb.favorite_feature}
                      </p>
                    )}
                    {fb.missing_feature && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                        <Lightbulb className="w-3 h-3 inline mr-1 text-yellow-500" />
                        <strong>Missing:</strong> {fb.missing_feature}
                      </p>
                    )}
                    {fb.bugs_encountered && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">
                        <Bug className="w-3 h-3 inline mr-1 text-red-500" />
                        <strong>Bugs:</strong> {fb.bugs_encountered}
                      </p>
                    )}
                    {fb.additional_comments && (
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        <MessageSquare className="w-3 h-3 inline mr-1 text-[#26C6DA]" />
                        {fb.additional_comments}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          {/* Users Table */}
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 dark:border-[#1F2937] flex flex-col md:flex-row gap-4 justify-between">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
            All Users
          </h3>
          <div className="flex gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                className="pl-9 w-64 bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937]"
                data-testid="search-users-input"
              />
            </div>
            <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(0); }}>
              <SelectTrigger className="w-32" data-testid="plan-filter-select">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-[#1F2937] bg-slate-100 dark:bg-[#1A1A2E]/30">
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">User</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Plan</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Goals</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Tasks</th>
                <th className="text-center py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Teams</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-slate-500 dark:text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u, index) => (
                <tr key={u.id} className="border-b border-slate-200 dark:border-[#1F2937]/50 hover:bg-slate-100 dark:bg-[#1A1A2E]/30" data-testid={`user-row-${index}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center text-white font-medium text-sm">
                        {u.name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800 dark:text-slate-100">{u.name}</span>
                          {u.is_admin && <Crown className="w-4 h-4 text-amber-500" title="Admin" />}
                        </div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPlanBadgeColor(u.plan || 'free')}`}>
                      {u.plan || 'free'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center text-slate-500 dark:text-slate-400">{u.goals_count || 0}</td>
                  <td className="py-3 px-4 text-center text-slate-500 dark:text-slate-400">{u.tasks_count || 0}</td>
                  <td className="py-3 px-4 text-center text-slate-500 dark:text-slate-400">{u.teams_count || 0}</td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      u.is_active !== false ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-100 text-red-700'
                    }`}>
                      {u.is_active !== false ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`user-actions-${index}-btn`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewUser(u.id)}>
                          View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdatePlan(u.id, 'free')}>
                          Set Free Plan
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdatePlan(u.id, 'pro')}>
                          Set Pro Plan
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdatePlan(u.id, 'enterprise')}>
                          Set Enterprise Plan
                        </DropdownMenuItem>
                        {u.id !== user.id && (
                          <>
                            <DropdownMenuItem onClick={() => handleToggleActive(u.id)}>
                              {u.is_active !== false ? 'Disable Account' : 'Enable Account'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggleAdmin(u.id)}>
                              {u.is_admin ? 'Remove Admin' : 'Make Admin'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => handleDeleteUser(u.id, u.name)}
                              className="text-red-600"
                            >
                              Delete User
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="p-4 border-t border-slate-200 dark:border-[#1F2937] flex items-center justify-between">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, totalUsers)} of {totalUsers} users
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * pageSize >= totalUsers}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
        </TabsContent>

        {/* Clients Management Tab */}
        <TabsContent value="clients" className="mt-6 space-y-6">
          {!selectedClient ? (
            <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  <UserPlus className="w-5 h-5 text-[#26C6DA]" />
                  Client Management
                </h3>
                <Button
                  onClick={() => {
                    setClientForm({ ...clientForm, password: generateRandomPassword() });
                    setShowCreateClient(true);
                  }}
                  className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                  data-testid="create-client-btn"
                >
                  <Plus className="w-4 h-4 mr-2" /> Create Client Account
                </Button>
              </div>
              
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Create and manage client accounts with their full strategic plans.
              </p>
              
              {/* Client List */}
              <div className="space-y-2">
                {users.filter(u => u.is_client === true).map(client => (
                  <div 
                    key={client.id}
                    className="p-4 bg-slate-50 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937] flex justify-between items-center cursor-pointer hover:border-[#26C6DA] transition-colors"
                    onClick={() => {
                      setSelectedClient(client.id);
                      loadClientProfile(client.id);
                    }}
                  >
                    <div>
                      <p className="font-medium text-slate-800 dark:text-slate-100">{client.name}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{client.email}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(client.plan || 'free')}`}>
                        {client.plan}
                      </span>
                      <Eye className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Back Button & Client Header */}
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedClient(null);
                    setClientProfile(null);
                  }}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back to Clients
                </Button>
                {clientProfile && (
                  <div>
                    <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                      {clientProfile.user.name}
                    </h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400">{clientProfile.user.email}</p>
                  </div>
                )}
              </div>

              {clientLoading ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-[#26C6DA]" />
                </div>
              ) : clientProfile && (
                <>
                  {/* Client Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-[#26C6DA]">{clientProfile.stats.goals_count}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Goals</p>
                    </div>
                    <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{clientProfile.stats.goals_completed}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Completed</p>
                    </div>
                    <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-blue-400">{clientProfile.stats.tasks_count}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Tasks</p>
                    </div>
                    <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-xl p-4 text-center">
                      <p className="text-2xl font-bold text-purple-400">{clientProfile.stats.prompts_answered}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">Reflections</p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowResetPassword(true)}
                      className="rounded-full"
                    >
                      Reset Password
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdatePlan(selectedClient, clientProfile.user.plan === 'enterprise' ? 'pro' : 'enterprise')}
                      className="rounded-full"
                    >
                      Toggle Plan
                    </Button>
                  </div>

                  {/* Vision & Mission */}
                  <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
                    <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                      <Target className="w-5 h-5 text-[#26C6DA]" />
                      Vision & Mission
                    </h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label className="text-slate-700 dark:text-slate-300">Vision</Label>
                        <Input
                          placeholder="Client's vision title"
                          value={clientProfile.strategic_objectives?.vision?.title || ''}
                          onChange={(e) => setClientProfile({
                            ...clientProfile,
                            strategic_objectives: {
                              ...clientProfile.strategic_objectives,
                              vision: { ...clientProfile.strategic_objectives?.vision, title: e.target.value }
                            }
                          })}
                          className="mt-1 bg-slate-50 dark:bg-[#1A1A2E]"
                        />
                        <textarea
                          placeholder="Vision description..."
                          value={clientProfile.strategic_objectives?.vision?.description || ''}
                          onChange={(e) => setClientProfile({
                            ...clientProfile,
                            strategic_objectives: {
                              ...clientProfile.strategic_objectives,
                              vision: { ...clientProfile.strategic_objectives?.vision, description: e.target.value }
                            }
                          })}
                          className="mt-2 w-full p-3 rounded-lg bg-slate-50 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 text-sm"
                          rows={3}
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700 dark:text-slate-300">Mission</Label>
                        <Input
                          placeholder="Client's mission title"
                          value={clientProfile.strategic_objectives?.mission?.title || ''}
                          onChange={(e) => setClientProfile({
                            ...clientProfile,
                            strategic_objectives: {
                              ...clientProfile.strategic_objectives,
                              mission: { ...clientProfile.strategic_objectives?.mission, title: e.target.value }
                            }
                          })}
                          className="mt-1 bg-slate-50 dark:bg-[#1A1A2E]"
                        />
                        <textarea
                          placeholder="Mission description..."
                          value={clientProfile.strategic_objectives?.mission?.description || ''}
                          onChange={(e) => setClientProfile({
                            ...clientProfile,
                            strategic_objectives: {
                              ...clientProfile.strategic_objectives,
                              mission: { ...clientProfile.strategic_objectives?.mission, description: e.target.value }
                            }
                          })}
                          className="mt-2 w-full p-3 rounded-lg bg-slate-50 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 text-sm"
                          rows={3}
                        />
                      </div>
                    </div>
                    <Button 
                      onClick={handleUpdateClientStrategic}
                      className="mt-4 bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                    >
                      Save Vision & Mission
                    </Button>
                  </div>

                  {/* Strategic Priorities */}
                  <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                        <Star className="w-5 h-5 text-amber-400" />
                        Strategic Priorities
                      </h3>
                      <Button
                        size="sm"
                        onClick={() => setShowAddPriority(true)}
                        className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Priority
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {(clientProfile.strategic_objectives?.priorities || []).map((priority, index) => (
                        <div key={priority.id} className="p-3 bg-slate-50 dark:bg-[#1A1A2E] rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium text-slate-800 dark:text-slate-100">{index + 1}. {priority.title}</p>
                            {priority.description && <p className="text-sm text-slate-500 dark:text-slate-400">{priority.description}</p>}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClientPriority(priority.id)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {(clientProfile.strategic_objectives?.priorities || []).length === 0 && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No priorities set yet</p>
                      )}
                    </div>
                  </div>

                  {/* Goals */}
                  <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                        <Target className="w-5 h-5 text-emerald-400" />
                        Goals ({clientProfile.goals.length})
                      </h3>
                      <Button
                        size="sm"
                        onClick={() => setShowAddGoal(true)}
                        className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Goal
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {clientProfile.goals.map(goal => (
                        <div key={goal.id} className="p-3 bg-slate-50 dark:bg-[#1A1A2E] rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium text-slate-800 dark:text-slate-100">{goal.title}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {goal.status} • {goal.key_results?.length || 0} key results
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClientGoal(goal.id)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {clientProfile.goals.length === 0 && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No goals created yet</p>
                      )}
                    </div>
                  </div>

                  {/* Tasks */}
                  <div className="bg-white dark:bg-[#111827] border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                        <CheckSquare className="w-5 h-5 text-blue-400" />
                        Tasks ({clientProfile.tasks.length})
                      </h3>
                      <Button
                        size="sm"
                        onClick={() => setShowAddTask(true)}
                        className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Task
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {clientProfile.tasks.map(task => (
                        <div key={task.id} className="p-3 bg-slate-50 dark:bg-[#1A1A2E] rounded-lg flex justify-between items-center">
                          <div>
                            <p className="font-medium text-slate-800 dark:text-slate-100">{task.title}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              {task.status} • {task.priority} priority
                              {task.due_date && ` • Due ${task.due_date}`}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClientTask(task.id)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      {clientProfile.tasks.length === 0 && (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">No tasks created yet</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Create Client Dialog */}
          <Dialog open={showCreateClient} onOpenChange={setShowCreateClient}>
            <DialogContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="text-slate-800 dark:text-slate-100">Create Client Account</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Name</Label>
                  <Input
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    placeholder="Client's full name"
                    className="bg-slate-50 dark:bg-[#1A1A2E]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    placeholder="client@example.com"
                    className="bg-slate-50 dark:bg-[#1A1A2E]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Temporary Password</Label>
                  <div className="flex gap-2">
                    <Input
                      value={clientForm.password}
                      onChange={(e) => setClientForm({ ...clientForm, password: e.target.value })}
                      className="bg-slate-50 dark:bg-[#1A1A2E]"
                      required
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setClientForm({ ...clientForm, password: generateRandomPassword() })}
                      className="rounded-full"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Plan</Label>
                  <Select value={clientForm.plan} onValueChange={(v) => setClientForm({ ...clientForm, plan: v })}>
                    <SelectTrigger className="bg-slate-50 dark:bg-[#1A1A2E]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="free">Free</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="send_email"
                    checked={clientForm.send_welcome_email}
                    onChange={(e) => setClientForm({ ...clientForm, send_welcome_email: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="send_email" className="text-slate-700 dark:text-slate-300">Send welcome email with credentials</Label>
                </div>
                <Button
                  type="submit"
                  disabled={creatingClient}
                  className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                >
                  {creatingClient ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Create Account
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add Priority Dialog */}
          <Dialog open={showAddPriority} onOpenChange={setShowAddPriority}>
            <DialogContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="text-slate-800 dark:text-slate-100">Add Strategic Priority</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddClientPriority} className="space-y-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Priority Title</Label>
                  <Input
                    value={priorityForm.title}
                    onChange={(e) => setPriorityForm({ ...priorityForm, title: e.target.value })}
                    placeholder="e.g., Grow Revenue by 50%"
                    className="bg-slate-50 dark:bg-[#1A1A2E]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Description</Label>
                  <textarea
                    value={priorityForm.description}
                    onChange={(e) => setPriorityForm({ ...priorityForm, description: e.target.value })}
                    placeholder="Describe this priority..."
                    className="w-full p-3 rounded-lg bg-slate-50 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                    rows={3}
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Target Date (optional)</Label>
                  <Input
                    type="date"
                    value={priorityForm.target_date}
                    onChange={(e) => setPriorityForm({ ...priorityForm, target_date: e.target.value })}
                    className="bg-slate-50 dark:bg-[#1A1A2E]"
                  />
                </div>
                <Button type="submit" className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full">
                  Add Priority
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add Goal Dialog */}
          <Dialog open={showAddGoal} onOpenChange={setShowAddGoal}>
            <DialogContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="text-slate-800 dark:text-slate-100">Add Goal</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddClientGoal} className="space-y-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Goal Title</Label>
                  <Input
                    value={goalForm.title}
                    onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })}
                    placeholder="e.g., Launch new product line"
                    className="bg-slate-50 dark:bg-[#1A1A2E]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Description</Label>
                  <textarea
                    value={goalForm.description}
                    onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                    placeholder="Describe this goal..."
                    className="w-full p-3 rounded-lg bg-slate-50 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Category</Label>
                    <Select value={goalForm.category} onValueChange={(v) => setGoalForm({ ...goalForm, category: v })}>
                      <SelectTrigger className="bg-slate-50 dark:bg-[#1A1A2E]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="strategic">Strategic</SelectItem>
                        <SelectItem value="operational">Operational</SelectItem>
                        <SelectItem value="financial">Financial</SelectItem>
                        <SelectItem value="personal">Personal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Due Date</Label>
                    <Input
                      type="date"
                      value={goalForm.due_date}
                      onChange={(e) => setGoalForm({ ...goalForm, due_date: e.target.value })}
                      className="bg-slate-50 dark:bg-[#1A1A2E]"
                    />
                  </div>
                </div>
                {clientProfile && clientProfile.strategic_objectives?.priorities?.length > 0 && (
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Link to Priority</Label>
                    <Select value={goalForm.priority_id} onValueChange={(v) => setGoalForm({ ...goalForm, priority_id: v })}>
                      <SelectTrigger className="bg-slate-50 dark:bg-[#1A1A2E]">
                        <SelectValue placeholder="Select priority (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {clientProfile.strategic_objectives.priorities.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full">
                  Add Goal
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Add Task Dialog */}
          <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
            <DialogContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="text-slate-800 dark:text-slate-100">Add Task</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddClientTask} className="space-y-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Task Title</Label>
                  <Input
                    value={taskForm.title}
                    onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                    placeholder="e.g., Review quarterly reports"
                    className="bg-slate-50 dark:bg-[#1A1A2E]"
                    required
                  />
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Description</Label>
                  <textarea
                    value={taskForm.description}
                    onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    placeholder="Task details..."
                    className="w-full p-3 rounded-lg bg-slate-50 dark:bg-[#1A1A2E] border border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100"
                    rows={2}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Priority</Label>
                    <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                      <SelectTrigger className="bg-slate-50 dark:bg-[#1A1A2E]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Due Date</Label>
                    <Input
                      type="date"
                      value={taskForm.due_date}
                      onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                      className="bg-slate-50 dark:bg-[#1A1A2E]"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">Recurring</Label>
                  <Select value={taskForm.recurring || ''} onValueChange={(v) => setTaskForm({ ...taskForm, recurring: v || null })}>
                    <SelectTrigger className="bg-slate-50 dark:bg-[#1A1A2E]">
                      <SelectValue placeholder="Not recurring" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Not recurring</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {clientProfile && clientProfile.goals.length > 0 && (
                  <div>
                    <Label className="text-slate-700 dark:text-slate-300">Link to Goal</Label>
                    <Select value={taskForm.goal_id || ''} onValueChange={(v) => setTaskForm({ ...taskForm, goal_id: v || null })}>
                      <SelectTrigger className="bg-slate-50 dark:bg-[#1A1A2E]">
                        <SelectValue placeholder="Select goal (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">None</SelectItem>
                        {clientProfile.goals.map(g => (
                          <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full">
                  Add Task
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          {/* Reset Password Dialog */}
          <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
            <DialogContent className="bg-white dark:bg-[#111827] border-slate-200 dark:border-[#1F2937]">
              <DialogHeader>
                <DialogTitle className="text-slate-800 dark:text-slate-100">Reset Client Password</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-700 dark:text-slate-300">New Password</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                      className="bg-slate-50 dark:bg-[#1A1A2E]"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setNewPassword(generateRandomPassword())}
                      className="rounded-full"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={handleResetClientPassword}
                  disabled={!newPassword.trim()}
                  className="w-full bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                >
                  Reset Password
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Promo Codes Tab */}
        <TabsContent value="promo" className="mt-6">
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-[#1F2937] flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Ticket className="w-5 h-5 text-[#26C6DA]" />
                Promo Codes
              </h3>
              <Button 
                onClick={() => setShowCreatePromo(true)}
                className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                data-testid="create-promo-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Code
              </Button>
            </div>
            
            {promoLoading ? (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">Loading...</div>
            ) : promoCodes.length === 0 ? (
              <div className="p-8 text-center text-slate-500 dark:text-slate-400">
                <Ticket className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No promo codes created yet</p>
                <p className="text-sm mt-1">Create a promo code to give users trial access</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-[#1F2937]">
                      <th className="p-4 font-medium">Code</th>
                      <th className="p-4 font-medium">Plan</th>
                      <th className="p-4 font-medium">Duration</th>
                      <th className="p-4 font-medium">Redemptions</th>
                      <th className="p-4 font-medium">Expires</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promoCodes.map((promo) => {
                      const isExpired = promo.expires_at && new Date(promo.expires_at) < new Date();
                      const isMaxed = promo.max_redemptions !== -1 && promo.redemption_count >= promo.max_redemptions;
                      
                      return (
                        <tr key={promo.id} className="border-b border-slate-200 dark:border-[#1F2937] last:border-0">
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <code className="px-2 py-1 bg-slate-100 dark:bg-[#1A1A2E] rounded text-sm font-mono text-slate-800 dark:text-slate-100">
                                {promo.code}
                              </code>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => copyToClipboard(promo.code)}
                                className="h-6 w-6 p-0"
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                              promo.plan === 'enterprise' ? 'bg-purple-500/20 text-purple-400' :
                              promo.plan === 'pro' ? 'bg-[#26C6DA]/20 text-[#26C6DA]' :
                              'bg-blue-500/20 text-blue-400'
                            }`}>
                              {promo.plan}
                            </span>
                          </td>
                          <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                            {promo.duration_days} days
                          </td>
                          <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                            {promo.redemption_count} / {promo.max_redemptions === -1 ? '∞' : promo.max_redemptions}
                          </td>
                          <td className="p-4 text-sm text-slate-600 dark:text-slate-300">
                            {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString() : 'Never'}
                          </td>
                          <td className="p-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              !promo.is_active ? 'bg-slate-500/20 text-slate-400' :
                              isExpired ? 'bg-red-500/20 text-red-400' :
                              isMaxed ? 'bg-amber-500/20 text-amber-400' :
                              'bg-emerald-500/20 text-emerald-400'
                            }`}>
                              {!promo.is_active ? 'Disabled' : isExpired ? 'Expired' : isMaxed ? 'Maxed' : 'Active'}
                            </span>
                          </td>
                          <td className="p-4">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewRedemptions(promo.code)}
                                className="h-8 w-8 p-0"
                                title="View redemptions"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleTogglePromoCode(promo.code)}
                                className="h-8 w-8 p-0"
                                title={promo.is_active ? 'Disable' : 'Enable'}
                              >
                                {promo.is_active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4 text-slate-400" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditPromoCode(promo)}
                                className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600"
                                title="Edit limits"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePromoCode(promo.code)}
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Create Promo Code Dialog */}
        <Dialog open={showCreatePromo} onOpenChange={setShowCreatePromo}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-[#26C6DA]" />
                Create Promo Code
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePromoCode} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., WORKSHOP2026"
                  className="font-mono uppercase"
                  data-testid="promo-code-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Plan Level</Label>
                <Select value={promoForm.plan} onValueChange={(v) => setPromoForm({ ...promoForm, plan: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manager">Manager ($9/mo features)</SelectItem>
                    <SelectItem value="pro">Pro ($19/mo features)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($49/mo features)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  value={promoForm.duration_days}
                  onChange={(e) => setPromoForm({ ...promoForm, duration_days: parseInt(e.target.value) || 14 })}
                  min="1"
                  max="365"
                  data-testid="promo-duration-input"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">How long the user gets access after redeeming</p>
              </div>

              <div className="space-y-2">
                <Label>Max Redemptions</Label>
                <Input
                  type="number"
                  value={promoForm.max_redemptions === -1 ? '' : promoForm.max_redemptions}
                  onChange={(e) => setPromoForm({ ...promoForm, max_redemptions: e.target.value ? parseInt(e.target.value) : -1 })}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>

              <div className="space-y-2">
                <Label>Code Expires On (optional)</Label>
                <Input
                  type="date"
                  value={promoForm.expires_at}
                  onChange={(e) => setPromoForm({ ...promoForm, expires_at: e.target.value })}
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">When the code itself becomes invalid</p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreatePromo(false)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={creatingPromo}
                  className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                  data-testid="create-promo-submit-btn"
                >
                  {creatingPromo ? 'Creating...' : 'Create Code'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Promo Code Dialog */}
        <Dialog open={!!editingPromo} onOpenChange={() => setEditingPromo(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Pencil className="w-5 h-5 text-[#26C6DA]" />
                Edit Promo Code: {editingPromo}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Max Redemptions</Label>
                <Input
                  type="number"
                  value={editPromoForm.max_redemptions === -1 ? '' : editPromoForm.max_redemptions}
                  onChange={(e) => setEditPromoForm({ 
                    ...editPromoForm, 
                    max_redemptions: e.target.value === '' ? -1 : parseInt(e.target.value) 
                  })}
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">Enter -1 or leave empty for unlimited</p>
              </div>

              <div className="space-y-2">
                <Label>Duration (days)</Label>
                <Input
                  type="number"
                  value={editPromoForm.duration_days}
                  onChange={(e) => setEditPromoForm({ 
                    ...editPromoForm, 
                    duration_days: parseInt(e.target.value) || 0 
                  })}
                  min="1"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditingPromo(null)} className="rounded-full">
                  Cancel
                </Button>
                <Button 
                  onClick={handleSavePromoCode}
                  className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Redemptions Dialog */}
        <Dialog open={!!selectedPromoRedemptions} onOpenChange={() => setSelectedPromoRedemptions(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Redemptions for {selectedPromoRedemptions}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {redemptionsData.length === 0 ? (
                <p className="text-center text-slate-500 dark:text-slate-400 py-8">No redemptions yet</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {redemptionsData.map((r) => (
                    <div key={r.id} className="p-3 bg-slate-100 dark:bg-[#1A1A2E] rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-100">{r.user_email}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">
                            Redeemed: {new Date(r.redeemed_at).toLocaleString()}
                          </p>
                        </div>
                        <span className="text-xs px-2 py-1 bg-[#26C6DA]/20 text-[#26C6DA] rounded-full capitalize">
                          {r.plan_granted}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        Expires: {new Date(r.expires_at).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <TabsContent value="scheduler" className="mt-6">
          {/* Scheduler Status */}
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-[#1F2937] flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Scheduled Tasks
              </h3>
              <Button variant="outline" size="sm" onClick={loadSchedulerStatus}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh Status
              </Button>
            </div>
            
            {schedulerStatus ? (
              <div className="p-6 space-y-6">
                {/* Scheduler Running Status */}
                <div className="flex items-center gap-3 p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                  <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                  <span className={`font-medium ${schedulerStatus.scheduler_running ? 'text-emerald-400' : 'text-red-400'}`}>
                    Scheduler {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'}
                  </span>
                </div>

                {/* Jobs List */}
                <div className="space-y-4">
                  {schedulerStatus.jobs?.map((job) => (
                    <div key={job.id} className="p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-slate-800 dark:text-slate-100">{job.name}</h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 font-mono">{job.trigger}</p>
                          {job.next_run_time && (
                            <p className="text-xs text-[#26C6DA] mt-2">
                              Next run: {new Date(job.next_run_time).toLocaleString()}
                            </p>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleTriggerJob(job.id.includes('daily') ? 'daily' : 'weekly')}
                          disabled={triggeringJob !== null}
                          className="text-[#26C6DA] border-[#26C6DA]/30 hover:bg-[#26C6DA]/10"
                        >
                          {triggeringJob === (job.id.includes('daily') ? 'daily' : 'weekly') ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            'Run Now'
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Info Box */}
                <div className="p-4 bg-gradient-to-r from-[#8B5CF6]/10 to-[#26C6DA]/10 border border-[#8B5CF6]/20 rounded-xl">
                  <h4 className="font-medium text-slate-800 dark:text-slate-100 mb-2">Email Notifications</h4>
                  <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                    <li>• <strong>Daily Prompts:</strong> Sent at 7:00 AM UTC to Pro/Enterprise users</li>
                    <li>• <strong>Weekly Summary:</strong> Sent on Sundays at 8:00 AM UTC to Pro/Enterprise users</li>
                    <li>• Users can disable notifications in their billing settings</li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="w-6 h-6 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin mx-auto" />
                <p className="text-slate-500 dark:text-slate-400 mt-3">Loading scheduler status...</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="errors" className="mt-6">
          {/* Error Logs */}
          <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-slate-200 dark:border-[#1F2937] flex justify-between items-center">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                Error Logs
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={loadErrors} disabled={errorsLoading}>
                  <RefreshCw className={`w-4 h-4 mr-2 ${errorsLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {errors.length > 0 && (
                  <Button variant="outline" size="sm" onClick={clearErrors} className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
            
            {errorsLoading ? (
              <div className="p-8 text-center">
                <div className="w-6 h-6 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin mx-auto" />
              </div>
            ) : errors.length === 0 ? (
              <div className="p-12 text-center">
                <AlertTriangle className="w-12 h-12 text-slate-600 dark:text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 dark:text-slate-400">No errors recorded</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Your app is running smoothly!</p>
              </div>
            ) : (
              <div className="divide-y divide-[#EBE9E1]/50">
                {errors.map((error, idx) => (
                  <div key={error.id || idx} className="p-4 hover:bg-slate-100 dark:bg-[#1A1A2E]/30">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-mono">
                            {error.error_type || 'Error'}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{error.method}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">{error.path}</span>
                        </div>
                        <p className="text-sm text-slate-800 dark:text-slate-100 font-medium truncate">{error.error}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          {error.created_at ? new Date(error.created_at).toLocaleString() : 'Unknown time'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* User Details Modal */}
      <Dialog open={!!selectedUser} onOpenChange={() => setSelectedUser(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>User Details</DialogTitle>
          </DialogHeader>
          {userDetails && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] flex items-center justify-center text-white font-bold text-xl">
                  {userDetails.user.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100">{userDetails.user.name}</h3>
                    {userDetails.user.is_admin && <Crown className="w-5 h-5 text-amber-500" />}
                  </div>
                  <p className="text-slate-500 dark:text-slate-400">{userDetails.user.email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getPlanBadgeColor(userDetails.user.plan || 'free')}`}>
                      {userDetails.user.plan || 'free'}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      Joined {formatDate(userDetails.user.created_at)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#26C6DA] font-mono">{userDetails.stats.goals_count}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Goals ({userDetails.stats.goals_completed} done)</p>
                </div>
                <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#26C6DA] font-mono">{userDetails.stats.tasks_count}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Tasks ({userDetails.stats.tasks_completed} done)</p>
                </div>
                <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#26C6DA] font-mono">{userDetails.stats.teams_count}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Teams</p>
                </div>
                <div className="p-4 bg-slate-100 dark:bg-[#1A1A2E]/50 rounded-xl text-center">
                  <p className="text-2xl font-bold text-[#26C6DA] font-mono">{userDetails.stats.ai_conversations}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">AI Messages</p>
                </div>
              </div>

              <div className="flex gap-3">
                <Select 
                  value={userDetails.user.plan || 'free'} 
                  onValueChange={(v) => handleUpdatePlan(userDetails.user.id, v)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Change Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro ($19/mo)</SelectItem>
                    <SelectItem value="enterprise">Enterprise ($49/mo)</SelectItem>
                  </SelectContent>
                </Select>
                
                {userDetails.user.id !== user.id && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handleToggleActive(userDetails.user.id)}
                      className="gap-2"
                    >
                      <UserX className="w-4 h-4" />
                      {userDetails.user.is_active !== false ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDeleteUser(userDetails.user.id, userDetails.user.name)}
                      className="gap-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
